import {
  useState,
  useEffect,
  useCallback,
  type ChangeEvent,
  type DragEvent,
  type ReactNode,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { uploadBankFile } from "../api/transactions";
import type { ImportResult } from "../types/transaction";
import { useImportJobPoll } from "../hooks/useImportJobPoll";
import { ErrorMessage } from "./ErrorMessage";
import { ImportResultPanel } from "./ImportResultPanel";
import { AnimatedProgress } from "./ui/AnimatedProgress";
import { ConfettiPop } from "./ui/ConfettiPop";
import { CheckmarkIcon, FileIcon } from "./upload/UploadIcons";
import { UploadDropZone } from "./upload/UploadDropZone";
import { refusalSentence } from "../hooks/useServerAnswer";

interface FileUploadProps {
  clientId: number;
  bankAccount?: string;
  onUploadComplete: () => void;
}

type UploadStage = "idle" | "reading" | "uploading" | "processing" | "done";

// LF-164: the extension check says what the server says for a file it cannot read,
// word for word (backend/prime/integrations/ofx.py NOT_OFX_SENTENCE), so the client
// reads one sentence whichever side refuses the file.
const NOT_OFX_SENTENCE =
  "This file is not a bank statement in the format we read. On your bank's download " +
  "page, choose OFX, QFX, Quicken or Money as the format.";

const STAGE_LABELS: Record<UploadStage, string> = {
  idle: "",
  reading: "Reading file...",
  uploading: "Uploading...",
  processing: "Processing transactions...",
  // The progress bar is not drawn once done; the done panel speaks instead.
  done: "",
};

const STAGE_PROGRESS: Record<UploadStage, number> = {
  idle: 0,
  reading: 15,
  uploading: 50,
  processing: 80,
  done: 100,
};

export function FileUpload({
  clientId,
  bankAccount,
  onUploadComplete,
}: FileUploadProps): ReactNode {
  const [stage, setStage] = useState<UploadStage>("idle");
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  // The files sent this session: from the second on, each answer names its file.
  const [uploadCount, setUploadCount] = useState(0);
  const [lastFileName, setLastFileName] = useState("");
  const jobPoll = useImportJobPoll(clientId, onUploadComplete);
  const shownFileName = uploadCount > 1 ? lastFileName : null;

  const uploading = stage !== "idle" && stage !== "done";

  useEffect(() => {
    if (stage === "done" && result && isCleanImport(result)) {
      setShowConfetti(true);
    }
  }, [stage, result]);

  const handleConfettiComplete = useCallback(() => {
    setShowConfetti(false);
  }, []);

  async function processFile(file: File): Promise<void> {
    const name = file.name.toLowerCase();
    if (!name.endsWith(".ofx") && !name.endsWith(".qfx")) {
      setError(NOT_OFX_SENTENCE);
      return;
    }

    setSelectedFileName(file.name);
    setLastFileName(file.name);
    setUploadCount((count) => count + 1);
    setError("");
    setResult(null);
    setShowConfetti(false);

    // Stage 1: Reading
    setStage("reading");
    await delay(400);

    // Stage 2: Uploading
    setStage("uploading");

    try {
      const importResult = await uploadBankFile(clientId, file, bankAccount);

      // Stage 3: Processing
      setStage("processing");
      await delay(600);

      // Stage 4: Done
      setResult(importResult);
      setStage("done");
      onUploadComplete();
      jobPoll.follow(importResult);
    } catch (err) {
      setError(refusalSentence(err));
      setStage("idle");
      setSelectedFileName(null);
    }
  }

  async function handleFileChange(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    if (!file) return;
    await processFile(file);
    event.target.value = "";
  }

  function handleDragOver(event: DragEvent): void {
    event.preventDefault();
    setDragOver(true);
  }

  function handleDragLeave(): void {
    setDragOver(false);
  }

  async function handleDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    setDragOver(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      await processFile(file);
    }
  }

  function resetUpload(): void {
    setStage("idle");
    setSelectedFileName(null);
    setResult(null);
    setShowConfetti(false);
  }

  const stageLabel = STAGE_LABELS[stage];
  const stageProgress = STAGE_PROGRESS[stage];

  return (
    <div className="file-upload">
      {stage === "idle" && !result && (
        <UploadDropZone
          dragOver={dragOver}
          uploading={uploading}
          handleDragOver={handleDragOver}
          handleDragLeave={handleDragLeave}
          handleDrop={handleDrop}
          handleFileChange={handleFileChange}
        />
      )}

      <AnimatePresence mode="wait">
        {uploading && (
          <motion.div
            className="upload-progress-container"
            key="uploading"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
          >
            <div className="upload-progress-file">
              <FileIcon />
              <span className="upload-progress-filename">{selectedFileName}</span>
            </div>
            <AnimatedProgress value={stageProgress} label={stageLabel} />
          </motion.div>
        )}

        {stage === "done" && result && (
          <motion.div
            className="upload-done-container"
            key="done"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="upload-done-header">
              <CheckmarkIcon />
              <ConfettiPop trigger={showConfetti} onComplete={handleConfettiComplete} />
            </div>
            <motion.div
              className="import-result"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, delay: 0.3 }}
            >
              <ImportResultPanel result={result} fileName={shownFileName} />
            </motion.div>
            <motion.button
              className="upload-another-button"
              onClick={resetUpload}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              Upload another file
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {error && shownFileName && <p className="upload-file-name">{shownFileName}</p>}
      {error && <ErrorMessage message={error} onDismiss={() => setError("")} />}
      {jobPoll.refusal && <ErrorMessage message={jobPoll.refusal} />}
    </div>
  );
}

/**
 * True when the upload added rows and none failed: the only answer worth the
 * confetti. An all-duplicates file or one with unreadable rows keeps the
 * checkmark (the file was processed) without a cheer.
 */
function isCleanImport(result: ImportResult): boolean {
  return result.imported > 0 && result.errored === 0;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
